import { type Brand, type Car } from '@prisma/client'
import { useEffect, useState } from 'react'
import {
  type TypeOfPaymentType,
  type WarrantyDurationTypes,
  type WarrantyTypes,
} from 'types/enums'
import {
  calculateDiscountedPrice,
  calculateFinancedAmount,
  formatDiscountedPrice,
  formatPrice,
  getApplicableTIN,
} from '#app/shared/lib/price.ts'
import { Icon } from '#app/shared/ui/icons/Icon.tsx'
import Header from '#app/shared/ui/layout/Header.tsx'
import { warrantyPriceByWarrantyType } from '../../reservar/ui/warranty'
import CarSummary from './carSummary'

type PaymentDataType = {
  typeOfPayment: TypeOfPaymentType | undefined
  warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  activeNumber: number
  initialEntry: number
  hasPackOptima: boolean
  maxPackGamaEntry: number
}

type Props = {
  car: Car & { brand: Brand; images: { id: string }[] }
  showBanner: boolean
  paymentData?: PaymentDataType
}

function PriceInfo({
  car,
  paymentData,
  inCashPrice,
  financedPrice,
}: {
  car: Car & { brand: Brand }
  paymentData?: PaymentDataType
  inCashPrice: string
  financedPrice: number
}) {
  let financeQuota = 0

  if (paymentData) {
    const applicableTIN = getApplicableTIN(
      paymentData.hasPackOptima,
      car.annualInterestRate ?? 0,
      car.annualInterestRatePackGo,
    )

    const { quota } = calculateFinancedAmount({
      price: financedPrice ?? 0,
      warrantyData: paymentData.warrantyChoice,
      initialEntry: paymentData.initialEntry,
      months: paymentData.activeNumber,
      TIN: applicableTIN,
      hasPackOptima: paymentData.hasPackOptima,
    })

    financeQuota = quota
  }

  return (
    <div className="flex w-full justify-between">
      <div>
        <div className="text-base font-bold">
          {car.brand.name} {car.model}
        </div>
        <div className="text-subtitle2-normal text-[12px] text-neutral-700">
          {Boolean(
            !paymentData?.typeOfPayment ||
              paymentData?.typeOfPayment === 'Al contado',
          ) && (
            <>
              Al Contado <span className="font-bold">{inCashPrice}</span>
            </>
          )}{' '}
          {!paymentData?.typeOfPayment && '|'}
          {Boolean(
            !paymentData?.typeOfPayment ||
              paymentData?.typeOfPayment === 'Financiado',
          ) && (
            <>
              {' '}
              Financiado{' '}
              <span className="font-bold">
                {formatPrice(Math.round(financedPrice))}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center pr-2">
        {paymentData?.typeOfPayment === 'Financiado' && (
          <div>
            <span className="mr-1 text-[18px] font-bold text-primary">
              {formatPrice(Math.round(financeQuota))}
            </span>
            <span
              className={`
                text-subtitle2-normal text-[12px] font-normal text-neutral-500
              `}
            >
              /mes
            </span>
          </div>
        )}
        {paymentData?.typeOfPayment === 'Al contado' && (
          <div>
            <span className="text-[18px] font-bold text-primary">
              {formatDiscountedPrice(
                car.price,
                car.discount,
                warrantyPriceByWarrantyType({
                  ...paymentData.warrantyChoice,
                  hasPackOptima: paymentData.hasPackOptima,
                }),
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function DrawerHeader({
  car,
  paymentData,
  inCashPrice,
  financedPrice,
  open,
  onToggle,
}: {
  car: Car & { brand: Brand }
  paymentData?: PaymentDataType
  inCashPrice: string
  financedPrice: number
  open: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={`
        flex items-center justify-between border-b bg-white px-4 py-2

        sm:px-14
      `}
    >
      <PriceInfo
        car={car}
        paymentData={paymentData}
        inCashPrice={inCashPrice}
        financedPrice={financedPrice}
      />
      <button
        className={`
          transition-transform

          ${open ? 'rotate-180' : ''}
        `}
        onClick={onToggle}
      >
        <Icon
          name="basic/chevron-down"
          size="16px"
          className="text-neutral-600"
        />
      </button>
    </div>
  )
}

export default function CarSummaryDrawerMobile({
  car,
  showBanner = true,
  paymentData,
}: Props) {
  const minHeight = 58
  const maxHeight = 86
  const [open, setOpen] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(minHeight)

  const financedPrice = calculateDiscountedPrice(
    car.minFinancedAmount ?? 0,
    car.discount,
  )
  const inCashPrice = formatDiscountedPrice(car.price, car.discount)

  useEffect(() => {
    const handleScroll = () => {
      setHeaderHeight(window.scrollY > 0 ? minHeight : maxHeight)
    }

    if (showBanner) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()
    }

    return () => window.removeEventListener('scroll', handleScroll)
  }, [showBanner])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      {/* Sticky Header */}
      <div
        className={`
          sticky z-30 block w-full bg-white

          lg:hidden
        `}
        style={{ top: `${showBanner ? headerHeight : minHeight}px` }}
      >
        <DrawerHeader
          car={car}
          paymentData={paymentData}
          inCashPrice={inCashPrice}
          financedPrice={financedPrice ?? 0}
          open={open}
          onToggle={() => setOpen(true)}
        />
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-white">
          <div>
            <Header isSticky={false} showBanner={showBanner} />
            <DrawerHeader
              car={car}
              paymentData={paymentData}
              inCashPrice={inCashPrice}
              financedPrice={financedPrice ?? 0}
              open={open}
              onToggle={() => setOpen(false)}
            />
            <div>
              <CarSummary
                car={car}
                className="w-full rounded-none"
                paymentData={paymentData}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
